import React, { useState, useEffect } from 'react';
import { useGlobalPerformance } from '../hooks/usePerformanceManager';
import performanceManager from '../utils/performanceManager';

/**
 * Offline Support Component
 * Provides offline functionality with cached data and sync capabilities
 */
const OfflineSupport = ({ children }) => {
  const { isOnline } = useGlobalPerformance();
  const [showOfflineMessage, setShowOfflineMessage] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, syncing, completed, failed
  const [cachedDataCount, setCachedDataCount] = useState(0);

  useEffect(() => {
    // Show offline message when going offline
    if (!isOnline) {
      setShowOfflineMessage(true);
      // Auto-hide after 5 seconds
      const timer = setTimeout(() => {
        setShowOfflineMessage(false);
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      // When coming back online, start sync
      if (showOfflineMessage) {
        setSyncStatus('syncing');
        if (performanceManager?.syncOfflineData) {
          performanceManager.syncOfflineData();
        }
      }
    }
  }, [isOnline, showOfflineMessage]);

  useEffect(() => {
    // Listen for sync events
    const handleSyncCompleted = (event) => {
      setSyncStatus('completed');
      setTimeout(() => setSyncStatus('idle'), 3000);
    };

    const handleSyncFailed = () => {
      setSyncStatus('failed');
      setTimeout(() => setSyncStatus('idle'), 5000);
    };

    // Update cached data count
    const updateCachedCount = () => {
      try {
        const report = performanceManager?.getPerformanceReport?.() || { offline: { cachedItems: 0 } };
        setCachedDataCount(report.offline?.cachedItems || 0);
      } catch (error) {
        // Fallback for test environment
        setCachedDataCount(0);
      }
    };

    window.addEventListener('sync-completed', handleSyncCompleted);
    window.addEventListener('sync-failed', handleSyncFailed);
    
    // Update cached count periodically
    updateCachedCount();
    const interval = setInterval(updateCachedCount, 30000);

    return () => {
      window.removeEventListener('sync-completed', handleSyncCompleted);
      window.removeEventListener('sync-failed', handleSyncFailed);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      {children}
      
      {/* Offline Status Banner */}
      {!isOnline && (
        <div className="offline-banner">
          <div className="offline-content">
            <i className="fas fa-wifi-slash"></i>
            <span>You're offline</span>
            {cachedDataCount > 0 && (
              <span className="cached-info">
                • {cachedDataCount} items cached
              </span>
            )}
          </div>
        </div>
      )}

      {/* Offline Message Toast */}
      {showOfflineMessage && (
        <div className="offline-toast">
          <div className="toast-content">
            <i className="fas fa-wifi-slash"></i>
            <div className="toast-text">
              <strong>You're now offline</strong>
              <p>Some features may be limited, but you can still browse cached content.</p>
            </div>
            <button 
              className="toast-close"
              onClick={() => setShowOfflineMessage(false)}
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
        </div>
      )}

      {/* Sync Status Toast */}
      {syncStatus !== 'idle' && (
        <div className={`sync-toast ${syncStatus}`}>
          <div className="toast-content">
            {syncStatus === 'syncing' && (
              <>
                <div className="sync-spinner"></div>
                <span>Syncing offline changes...</span>
              </>
            )}
            {syncStatus === 'completed' && (
              <>
                <i className="fas fa-check-circle"></i>
                <span>Sync completed successfully</span>
              </>
            )}
            {syncStatus === 'failed' && (
              <>
                <i className="fas fa-exclamation-triangle"></i>
                <span>Sync failed - will retry automatically</span>
              </>
            )}
          </div>
        </div>
      )}

      <style>{`
        .offline-banner {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          background: #ff9800;
          color: white;
          padding: 8px 16px;
          z-index: 1000;
          text-align: center;
          font-size: 14px;
          font-weight: 500;
        }

        .offline-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .cached-info {
          font-size: 12px;
          opacity: 0.9;
        }

        .offline-toast {
          position: fixed;
          top: 20px;
          right: 20px;
          background: #333;
          color: white;
          padding: 16px;
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          z-index: 1001;
          max-width: 350px;
          animation: slideIn 0.3s ease;
        }

        .toast-content {
          display: flex;
          align-items: flex-start;
          gap: 12px;
        }

        .toast-text {
          flex: 1;
        }

        .toast-text strong {
          display: block;
          margin-bottom: 4px;
        }

        .toast-text p {
          margin: 0;
          font-size: 14px;
          opacity: 0.9;
        }

        .toast-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 4px;
          opacity: 0.7;
          transition: opacity 0.2s;
        }

        .toast-close:hover {
          opacity: 1;
        }

        .sync-toast {
          position: fixed;
          bottom: 20px;
          right: 20px;
          padding: 12px 16px;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          z-index: 1001;
          animation: slideUp 0.3s ease;
        }

        .sync-toast.syncing {
          background: #2196F3;
        }

        .sync-toast.completed {
          background: #4CAF50;
        }

        .sync-toast.failed {
          background: #f44336;
        }

        .sync-toast .toast-content {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .sync-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .offline-toast {
            top: 10px;
            right: 10px;
            left: 10px;
            max-width: none;
          }

          .sync-toast {
            bottom: 10px;
            right: 10px;
            left: 10px;
          }

          .offline-banner {
            padding: 6px 12px;
            font-size: 13px;
          }
        }
      `}</style>
    </>
  );
};

/**
 * Hook for offline data management
 */
export const useOfflineData = (key, fetchFunction, options = {}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const { isOnline } = useGlobalPerformance();

  const { 
    ttl = 3600000, // 1 hour default
    retryOnline = true,
    fallbackToCache = true 
  } = options;

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Try to get cached data first
        const cachedData = performanceManager?.getCachedData?.(key);
        
        if (cachedData && (!isOnline || fallbackToCache)) {
          setData(cachedData);
          setIsFromCache(true);
          setLoading(false);
          
          // If offline, don't try to fetch
          if (!isOnline) {
            return;
          }
        }

        // Try to fetch fresh data if online
        if (isOnline && fetchFunction) {
          try {
            const freshData = await fetchFunction();
            setData(freshData);
            setIsFromCache(false);
            
            // Cache the fresh data
            if (performanceManager?.cacheData) {
              performanceManager.cacheData(key, freshData, ttl);
            }
          } catch (fetchError) {
            // If fetch fails but we have cached data, use it
            if (cachedData) {
              setData(cachedData);
              setIsFromCache(true);
            } else {
              throw fetchError;
            }
          }
        } else if (!cachedData) {
          throw new Error('No cached data available and currently offline');
        }
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [key, isOnline, fetchFunction, ttl, fallbackToCache]);

  // Retry when coming back online
  useEffect(() => {
    if (isOnline && retryOnline && error) {
      const timer = setTimeout(() => {
        setError(null);
        setLoading(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, retryOnline, error]);

  const refresh = () => {
    setLoading(true);
    setError(null);
  };

  return {
    data,
    loading,
    error,
    isFromCache,
    isOnline,
    refresh
  };
};

export default OfflineSupport;